import { useMemo, useState } from 'react';
import {
  Table,
  ActionIcon,
  Text,
  Menu,
  rem,
  TextInput,
  Paper,
  Stack,
  Badge,
  Group,
  Tooltip,
} from '@mantine/core';
import { IconDotsVertical, IconEdit, IconTrash, IconSearch } from '@tabler/icons-react';
import type { CustomFieldDefinition } from '@/hooks/api/useCustomFields';
import { useCategories } from '@/hooks/api/useCategories';
import { useOrganization } from '@/hooks/useOrganization';

interface CustomFieldsTableProps {
  fields: CustomFieldDefinition[];
  onEdit: (field: CustomFieldDefinition) => void;
  onDelete: (field: CustomFieldDefinition) => void;
  isLoading?: boolean;
}

const fieldTypeLabels: Record<string, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  dropdown: 'Dropdown',
  textarea: 'Text Area',
  checkbox: 'Checkbox',
};

const fieldTypeColors: Record<string, string> = {
  text: 'blue',
  number: 'cyan',
  date: 'grape',
  dropdown: 'teal',
  textarea: 'indigo',
  checkbox: 'orange',
};

export function CustomFieldsTable({
  fields,
  onEdit,
  onDelete,
  isLoading,
}: CustomFieldsTableProps) {
  const { organizationId } = useOrganization();
  const { data: categories = [] } = useCategories(organizationId);
  const [search, setSearch] = useState('');

  // Create a map of category ID to name for quick lookup
  const categoryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((cat) => map.set(cat.id, cat.name));
    return map;
  }, [categories]);

  const filteredFields = useMemo(() => {
    return fields.filter((field) => {
      const matchesSearch =
        search === '' || field.name.toLowerCase().includes(search.toLowerCase());
      return matchesSearch;
    });
  }, [fields, search]);

  const rows = filteredFields.map((field) => (
    <Table.Tr key={field.id}>
      <Table.Td>
        <Text fw={500}>{field.name}</Text>
      </Table.Td>
      <Table.Td>
        <Badge color={fieldTypeColors[field.field_type] || 'gray'} variant="light">
          {fieldTypeLabels[field.field_type] || field.field_type}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Badge color={field.is_required ? 'red' : 'gray'} variant="light" size="sm">
          {field.is_required ? 'Required' : 'Optional'}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Badge color={field.is_active ? 'green' : 'gray'} variant="light" size="sm">
          {field.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </Table.Td>
      <Table.Td>
        {(!field.category_ids || field.category_ids.length === 0) ? (
          <Badge variant="light" color="gray" size="sm">All</Badge>
        ) : field.category_ids.length === 1 ? (
          <Badge variant="light" color="violet" size="sm">
            {categoryNameMap.get(field.category_ids[0]) || 'Unknown'}
          </Badge>
        ) : (
          <Tooltip
            label={field.category_ids.map((id) => categoryNameMap.get(id) || 'Unknown').join(', ')}
            withArrow
            multiline
            maw={300}
          >
            <Badge variant="light" color="violet" size="sm" style={{ cursor: 'help' }}>
              {field.category_ids.length} categories
            </Badge>
          </Tooltip>
        )}
      </Table.Td>
      <Table.Td>{field.display_order}</Table.Td>
      <Table.Td>
        <Group gap="xs" justify="flex-end">
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => onEdit(field)}
            title="Edit"
          >
            <IconEdit size={16} />
          </ActionIcon>
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                color="red"
                leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                onClick={() => onDelete(field)}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="md">
      <Paper p="md" withBorder>
        <TextInput
          placeholder="Search custom fields..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ maxWidth: 400 }}
        />
      </Paper>

      <Paper withBorder>
        <Table.ScrollContainer minWidth={800}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Field Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Required</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Categories</Table.Th>
                <Table.Th>Order</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text ta="center" py="xl" c="dimmed">
                      Loading custom fields...
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : rows.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text ta="center" py="xl" c="dimmed">
                      No custom fields found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                rows
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <Text size="sm" c="dimmed">
        Showing {filteredFields.length} of {fields.length} custom fields
      </Text>
    </Stack>
  );
}
